import { CreatorAction, CreatorActionType } from '@/types/types';
import { ProjectRenderer } from './ProjectRenderer';
import { Loader } from './Loader';
import { getFileSrc } from '@/utils/utilities';
import { RenderUtils } from './RenderUtils';
import { MapType } from '@/api/AssetApi';
import { AssetsLib } from './AssetsLib';
import {
	RepeatWrapping,
	SRGBColorSpace,
	LinearSRGBColorSpace,
	MeshStandardMaterial,
	Vector2,
	Object3D,
	Color,
} from 'three';

export class ActionHandler {
	private loader = Loader.getInstance();
	private assetsLib = AssetsLib.getInstance();

	constructor(private renderer: ProjectRenderer) {
		this.renderer = renderer;
	}

	async dispatchAction(action: CreatorAction) {
		switch (action.type) {
			case CreatorActionType.SET_MATERIAL:
				await this.handleMaterialAction(action);
				break;
			default:
				throw new Error('UNKNOWN_ACTION_TYPE');
		}
	}

	private async handleMaterialAction(action: CreatorAction) {
		const asset = action.payload.asset;
		if (!asset || !action.payload.target) return;

		const objectTargetName = action.payload.target;

		const matches: Object3D[] = [];
		this.renderer.product.traverse((obj) => {
			if (obj.userData?.target === objectTargetName) matches.push(obj);
		});

		const textures = await Promise.all(
			Object.values(MapType).map((type) => {
				if (!asset.maps[type]) return null;

				const cached = this.assetsLib.texturesCache.get(asset.maps[type].path);
				if (cached) return cached;

				return this.loader
					.loadTexture(getFileSrc(asset.maps[type].path))
					.then((texture) => {
						this.assetsLib.texturesCache.set(asset.maps[type]!.path, texture);

						return texture;
					});
			})
		);

		for (const match of matches) {
			if (!RenderUtils.isMesh(match)) return;

			const displacementScale = match.userData.displacement_scale ?? 0.0005;

			const cacheKey = `${asset.id}_${displacementScale}`;
			let material = this.assetsLib.materialCache.get(cacheKey);

			if (!material) {
				for (const texture of textures) {
					if (!texture) continue;

					texture.wrapS = RepeatWrapping;
					texture.wrapT = RepeatWrapping;
					texture.repeat.set(5, 5);
				}

				const [
					map,
					roughnessMap,
					normalMap,
					displacementMap,
					metalnessMap,
					aoMap,
					lightMap,
				] = textures;

				if (!map) {
					material = new MeshStandardMaterial({
						color: new Color(asset.options.color),
						metalness: asset.options.metalness ?? 0,
						roughness: asset.options.roughness ?? 0,
					});

					this.assetsLib.materialCache.set(cacheKey, material);
				} else {
					map.colorSpace = SRGBColorSpace;

					for (const texture of [
						roughnessMap,
						normalMap,
						displacementMap,
						metalnessMap,
						aoMap,
						lightMap,
					]) {
						if (!texture) continue;
						texture.colorSpace = LinearSRGBColorSpace;
					}

					material = new MeshStandardMaterial({
						map,
						normalScale: new Vector2(0.025, 0.025),
						displacementMap,
						displacementScale,
						metalnessMap,
						normalMap,
						roughnessMap,
						aoMap,
						lightMap,
					});

					this.assetsLib.materialCache.set(cacheKey, material);
				}
			}

			match.material = material;
		}
	}
}
